<template>
  <div class="bg-light">
    <!-- Main Content -->
    <main class="container my-4">
      <div class="row">
        <div class="col-lg-12 mb-4">
          <div class="bg-white rounded-lg shadow-lg p-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h2 class="font-weight-bold text-zebra mb-0">
                <i class="fas fa-receipt me-2"></i> Confirmación de Compra
              </h2>
            </div>

            <div class="alert alert-success" v-if="order.status === '3'">
              <i class="fas fa-check-circle"></i> 
              Su compra ha sido confirmada exitosamente. Hemos enviado los detalles a su correo electrónico.
            </div>

            <div class="d-flex justify-content-between mt-4 mb-3">
              <button class="btn btn-secondary" @click="goBack">
                <i class="fas fa-arrow-left me-2"></i> Volver
              </button>

              <!-- Nuevo botón para abrir PDF -->
              <a :href="`https://zebraasistencia.page/print/${orderId}`" target="_blank" class="btn btn-secondary">
                <i class="fas fa-file-pdf me-2"></i> Ver PDF
              </a>
            </div>

            <!-- Voucher Card -->
            <div class="card border-0 mb-4" id="voucherContent">
              <!-- Header -->
              <div class="card-header bg-zebra text-white p-3">
                <div class="d-flex justify-content-between">
                  <div>
                    <h3 class="mb-0">Voucher #{{ order.code }}</h3>
                    <small>Fecha: {{ formatDate(order.date) }}</small>
                  </div>

                </div>
              </div>

              <!-- Body -->
              <div class="card-body p-4">
                <!-- Trip Details Section -->
                <div class="mb-4">
                  <h4 class="border-bottom pb-2 text-zebra">Detalles del Viaje</h4>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <strong>Origen:</strong> {{ order.origin }}
                      </div>
                      <div class="mb-3">
                        <strong>Destino:</strong> {{ order.destination }}
                      </div>
                      <div class="mb-3">
                        <strong>Categoría:</strong> {{ order.category }}
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <strong>Fecha de inicio:</strong> {{ formatDate(order.startDate) }}
                      </div>
                      <div class="mb-3">
                        <strong>Fecha de fin:</strong> {{ formatDate(order.endDate) }}
                      </div>
                      <div class="mb-3">
                        <strong>Duración:</strong> {{ order.numberDays }} días
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Plan Details Section -->
                <div class="mb-4">
                  <h4 class="border-bottom pb-2 text-zebra">Detalles del Plan</h4>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <strong>Plan:</strong> {{ order.plan }}
                      </div>
                      <div class="mb-3">
                        <strong>Beneficiarios:</strong> {{ order.numBeneficiaries }}
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="alert alert-info mb-0">
                        <i class="fas fa-info-circle me-2"></i>
                        Para ver todos los detalles de la cobertura, por favor consulte los términos y condiciones adjuntos.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Beneficiaries Section (if any) -->
                <div class="mb-4" v-if="order.beneficiaries && order.beneficiaries.length > 0">
                  <h4 class="border-bottom pb-2 text-zebra">Beneficiarios</h4>
                  <div class="table-responsive">
                    <table class="table table-striped">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Documento</th>
                          <th>Fecha de nacimiento</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(beneficiary, index) in order.beneficiaries" :key="index">
                          <td>{{ beneficiary.name }}</td>
                          <td>{{ beneficiary.document }}</td>
                          <td>{{ formatDate(beneficiary.birthdate) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="mb-4" v-else>
                  <h4 class="border-bottom pb-2 text-zebra">Beneficiarios</h4>
                  <p class="text-muted">No se han registrado beneficiarios específicos.</p>
                </div>

                <!-- Contact Information Section -->
                <div class="mb-4">
                  <h4 class="border-bottom pb-2 text-zebra">Información de Contacto</h4>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <strong>Nombre:</strong> {{ order.contactName }}
                      </div>
                      <div class="mb-3">
                        <strong>Email:</strong> {{ order.contactEmail }}
                      </div>
                      <div class="mb-3">
                        <strong>Teléfono:</strong> {{ order.contactPhone }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Payment Details Section -->
                <div v-if="order.total !== undefined">
                  <h4 class="border-bottom pb-2 text-zebra">Resumen de Pago</h4>
                  <div class="table-responsive">
                    <table class="table">
                      <tbody>
                        <tr>
                          <td>Subtotal</td>
                          <td class="text-end">{{ convertCurrency(order.total) }}</td>
                        </tr>
                        <tr v-if="parseFloat(order.totalUpgrades) > 0">
                          <td>Upgrades</td>
                          <td class="text-end">{{ convertCurrency(order.totalUpgrades) }}</td>
                        </tr>
                        <tr v-if="parseFloat(order.totalDiscount) > 0">
                          <td>Descuentos</td>
                          <td class="text-end">-{{ convertCurrency(order.totalDiscount) }}</td>
                        </tr>
                        <tr v-if="parseFloat(order.totalTax) > 0">
                          <td>Impuestos</td>
                          <td class="text-end">{{ convertCurrency(order.totalTax) }}</td>
                        </tr>
                        <tr v-if="parseFloat(order.totalFee) > 0">
                          <td>Cargos adicionales</td>
                          <td class="text-end">{{ convertCurrency(order.totalFee) }}</td>
                        </tr>
                        <tr class="fw-bold">
                          <td>Total</td>
                          <td class="text-end">{{ convertCurrency(calculateTotal()) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Footer -->
            </div>

            <div class="d-flex justify-content-between mt-4">
              <button class="btn btn-secondary" @click="goBack">
                <i class="fas fa-arrow-left me-2"></i> Volver
              </button>
              <!-- Nuevo botón para abrir PDF -->
              <a :href="`https://zebraasistencia.page/print/${orderId}`" target="_blank" class="btn btn-zebra">
                <i class="fas fa-file-pdf me-2"></i> Ver PDF
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'VoucherView',
  data() {
    return {
      order: {},
      orderId: '',
      trm: 0
    }
  },
  props: {
    id: String
  },
  async mounted() {
    try {
      const id = this.$route.params.id || this.$route.query.id;
      this.orderId = id; // Guardar el ID para usar en el enlace del PDF
      this.trm = localStorage.getItem('trm');
      const response = await this.$root.get(`orders/order/${id}`);
      if (response.status === "OK") {
        this.order = response.data;
          console.log("🧾 Orden recibida:", this.order);
      } else {
          console.log('Error al cargar la información: ' + response);
      }
    } catch (err) {
        console.log('Error al cargar la información: ' + err.message);
    }
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const iso = date.toISOString().slice(0, 10); // "YYYY-MM-DD"
      return iso.replace(/-/g, '/'); // "YYYY/MM/DD"
    },
    convertCurrency(value) {
      const trm = parseFloat(this.trm || 1); // default a 1 si no ha cargado
      const val = parseFloat(value || 0);
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(val * trm) + '(COP)';
    },
    formatCurrency(value) {
      
      if (!value || value === '0.000') return `${this.order.currency} 0.00`;
      const numValue = parseFloat(value);
      
      return `${this.order.currency} ${numValue.toFixed(2)}`;
    },
    parseMoney(value) {
      const number = parseFloat(value);
      return isNaN(number) ? 0 : number;
    },
    calculateTotal() {
      return this.parseMoney(this.order.total)
          + this.parseMoney(this.order.totalTax)
          + this.parseMoney(this.order.totalFee)
          + this.parseMoney(this.order.totalUpgrades)
          - this.parseMoney(this.order.totalDiscount);
    },
    goBack() {
      this.$router.push('/');
    }
  }
}

</script>

<style scoped>
.bg-zebra {
  background-color: #20c997;
}

.text-zebra {
  color: #20c997;
}

.btn-zebra {
  background-color: #20c997;
  color: white;
}

.btn-zebra:hover {
  background-color: #17a2b8;
}

@media print {
  .no-print {
    display: none;
  }
  
  body {
    padding: 0;
    margin: 0;
  }
  
  .card {
    border: none !important;
    box-shadow: none !important;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
  }
}
</style>